import { db } from "./config.js"
import { useState, useEffect, useRef, useCallback} from 'react';
import { client } from '../hooks/Client';
import notDeleted from '../helpers/notDeleted';
import describeQueryError from '../helpers/firestore/describeQueryError';

// Operators that Firestore caps at 10 values — the engine chunks + merges these.
const CHUNKABLE_OPS = ['in', 'array-contains-any'];
const CHUNK_SIZE = 10;

// AI-CONTEXT: See docs/pitfalls.md#database-firestore-silent-listener-failure
// onSnapshot without an error callback swallows the failure: no snapshot ever
// arrives, so the list stays at its initial value and the UI renders an empty
// state. A missing index then looks exactly like "there is no data" — that cost
// an hour of debugging on the huishouden-afspraken tab. Always pass this.
// `report` krijgt de fout ook, zodat een scherm iets anders kan tonen dan een
// lege lijst. Zonder dat is een geweigerde read niet te onderscheiden van
// 'er is niets' — zie docs/inbox.md Bug #133.
const onQueryError = (collection, constraints, report) => (error) => {
    console.error(describeQueryError(collection, constraints, error), error);
    if (typeof report === 'function') report(error);
};

/**
 * @typedef {[string, string, any]} WhereClause  // [field, op, value]
 */

/**
 * The single Firestore read engine. Every named hook below is a thin wrapper
 * over this. Centralises what used to be copy-pasted across ~65 hooks:
 *   - tenant scoping (default `CompagnyID == client`)
 *   - the missing-value guard (skip the listener when a bound value is empty)
 *   - `in` / `array-contains-any` chunking (>10 values) with merge + dedup
 *   - snapshot -> array mapping (`{...data, docid}`)
 *   - **soft-delete filtering (ADR-008)** — deleted docs are dropped by default
 *
 * @template T
 * @param {string} collection - Firestore collection name.
 * @param {{
 *   where?: Array<WhereClause>,
 *   orderBy?: [string, 'asc'|'desc'] | null,
 *   limit?: number | null,
 *   scope?: WhereClause | 'none',
 *   includeDeleted?: boolean,
 *   enabled?: boolean,
 *   initial?: any,
 *   onError?: ((error: {code?: string, message?: string}) => void) | null,
 * }} [options]
 * @returns {Array<T>} Live docs (deleted filtered out unless `includeDeleted`).
 *
 * AI-CONTEXT: See docs/decisions.md (ADR-008 soft deletes),
 * docs/pitfalls.md#database-firestore-orderby-excludes-missing-field,
 * docs/pitfalls.md#database-firestore-null-is-a-valid-query-value,
 * docs/pitfalls.md#database-firestore-silent-listener-failure and
 * docs/pitfalls.md#state-useeffect-inline-filter
 */
const useFirestoreQuery = (collection, options = {}) => {
    const {
        where = [],
        orderBy = null,
        limit = null,
        scope = ['CompagnyID', '==', client],
        includeDeleted = false,
        enabled = true,
        initial = "",
        onError = null,
    } = options;

    const [docs, setDocs] = useState(initial);

    // In een ref, niet in de dependency-key: een callback die per render een
    // nieuwe identiteit krijgt mag de listener niet opnieuw opzetten, maar de
    // listener moet wel altijd de laatste versie aanroepen.
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    const reportError = useCallback((error) => {
        if (onErrorRef.current) onErrorRef.current(error);
    }, []);

    // Scope first, then the caller's where clauses.
    const constraints = scope === 'none' ? where : [scope, ...where];

    // Stable dependency key — never pass raw arrays/objects as effect deps
    // (pitfall state-useeffect-inline-filter re-subscribes on every render).
    const depKey = JSON.stringify({ collection, constraints, orderBy, limit, includeDeleted, enabled });

    useEffect(() => {
        if (!enabled || !collection) {
            setDocs(initial);
            return;
        }

        // AI-CONTEXT: See docs/pitfalls.md#database-firestore-null-is-a-valid-query-value
        // Missing-value guard: skip the listener when any bound value is
        // undefined or '' — `where(field,'==','')` otherwise opens a real
        // always-on subscription that returns nothing but counts as a read, and
        // `undefined` makes Firestore throw outright
        // (see docs/pitfalls.md#database-firestore-where-undefined-rejects).
        //
        // `null`, `false` and `0` are legitimate query values, NOT missing ones.
        // `Parent == null` is how the app finds top-level tasks — guarding it
        // away skipped the query entirely and rendered the huishouden Afspraken
        // tab, its dashboard card and the subtask list as silently empty.
        // Callers signal "no value yet" with '' (see Tasks.jsx: `adressId || ''`).
        //
        // AI-CONTEXT: See docs/pitfalls.md#database-empty-string-is-a-real-stored-value
        // Keerzijde: een collectie die '' als ECHTE waarde opslaat is hierlangs niet te
        // bevragen — de guard slaat de query dan stil over. Los dat clientside op (query
        // het gewone veld, filter '' in de component); verruim deze guard niet met een
        // opt-out, want dan betekent '' per aanroep iets anders.
        const hasMissing = constraints.some(([, , value]) =>
            value === undefined || value === '');
        if (hasMissing) {
            setDocs(initial);
            return;
        }

        // in / array-contains-any need a non-empty array; [] is an invalid query.
        const hasEmptyArrayOp = constraints.some(([, op, value]) =>
            CHUNKABLE_OPS.includes(op) && (!Array.isArray(value) || value.length === 0));
        if (hasEmptyArrayOp) {
            setDocs(initial);
            return;
        }

        const mapSnapshot = (querySnapshot) => {
            const docArray = [];
            querySnapshot.forEach((doc) => {
                const data = { ...doc.data(), docid: doc.id };
                if (includeDeleted || notDeleted(data)) docArray.push(data);
            });
            return docArray;
        };

        const buildQuery = (constraintList) => {
            let query = db.collection(collection);
            constraintList.forEach(([field, op, value]) => {
                query = query.where(field, op, value);
            });
            if (orderBy) query = query.orderBy(orderBy[0], orderBy[1]);
            if (limit) query = query.limit(limit);
            return query;
        };

        // Chunk when a CHUNKABLE op carries more than 10 values.
        const chunkIndex = constraints.findIndex(([, op, value]) =>
            CHUNKABLE_OPS.includes(op) && Array.isArray(value) && value.length > CHUNK_SIZE);

        if (chunkIndex === -1) {
            const unsub = buildQuery(constraints).onSnapshot(
                (querySnapshot) => setDocs(mapSnapshot(querySnapshot)),
                onQueryError(collection, constraints, reportError)
            );
            return () => unsub();
        }

        // One listener per chunk; merge + dedup by docid across chunks.
        const [, , chunkValue] = constraints[chunkIndex];
        const chunks = [];
        for (let i = 0; i < chunkValue.length; i += CHUNK_SIZE) {
            chunks.push(chunkValue.slice(i, i + CHUNK_SIZE));
        }
        const perChunk = chunks.map(() => []);
        const emit = () => {
            const seen = new Set();
            const merged = [];
            perChunk.forEach((arr) => arr.forEach((d) => {
                if (!seen.has(d.docid)) {
                    seen.add(d.docid);
                    merged.push(d);
                }
            }));
            setDocs(merged);
        };
        const unsubs = chunks.map((chunk, ci) => {
            const chunked = constraints.map((c, idx) =>
                idx === chunkIndex ? [c[0], c[1], chunk] : c);
            return buildQuery(chunked).onSnapshot(
                (querySnapshot) => {
                    perChunk[ci] = mapSnapshot(querySnapshot);
                    emit();
                },
                onQueryError(collection, chunked, reportError)
            );
        });
        return () => unsubs.forEach((u) => u && u());

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [depKey, reportError]);

    return docs;
};

// ---------------------------------------------------------------------------
// Compatibility wrappers. Each preserves its original signature and initial
// value; all delegate to useFirestoreQuery, which now applies the central
// soft-delete filter. New code should prefer useFirestoreQuery directly.
// ---------------------------------------------------------------------------

/**
 * @template T
 * @param {string} collection
 * @returns {Array<T>} All docs in the collection (no tenant scope).
 */
const useFirestoreAll = (collection) =>
    useFirestoreQuery(collection, { scope: 'none' });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {'asc' | 'desc'} order
 * @returns {Array<T>}
 */
const useFirestoreAllOrderBy = (collection, field, order) =>
    useFirestoreQuery(collection, { scope: 'none', orderBy: [field, order] });

/**
 * @template T
 * @param {string} collection
 * @param {string} key
 * @param {string} value
 * @returns {Array<T>}
 */
const useFirestoreAllArrayContains = (collection, key, value) =>
    useFirestoreQuery(collection, { scope: 'none', where: [[key, 'array-contains', value]] });

/**
 * @template T
 * @param {string} collection
 * @param {string | boolean} id
 * @returns {Array<T>}
 */
const useFirestoreAllID = (collection, id) =>
    useFirestoreQuery(collection, { scope: 'none', where: [['ID', '==', id]] });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {Array<string>} ids
 * @returns {Array<T>}
 */
const useFirestoreAllIn = (collection, field, ids) =>
    useFirestoreQuery(collection, { scope: 'none', where: [[field, 'in', ids]], initial: [] });

// Tasks for any of the given member IDs, scoped by tenant, from lookbackStart.
// Firebase 8 caps `array-contains-any` at 10 — the engine chunks + merges.
const useFirestoreTasksForMembers = (memberIds, companyId, lookbackStart) =>
    useFirestoreQuery('Tasks', {
        scope: ['CompagnyID', '==', companyId],
        where: [
            ['TeamMembers', 'array-contains-any', memberIds],
            ['Timestamp', '>=', lookbackStart],
        ],
        initial: [],
    });

// PersonaAbsences for any of the given persona IDs, scoped by tenant.
const useFirestorePersonaAbsences = (personaIds, companyId) =>
    useFirestoreQuery('PersonaAbsences', {
        scope: ['CompagnyID', '==', companyId],
        where: [['PersonaID', 'in', personaIds]],
        initial: [],
    });

// personaSchedule Tasks for any of the given persona IDs, scoped by tenant.
const useFirestorePersonaSchedules = (personaIds, companyId) =>
    useFirestoreQuery('Tasks', {
        scope: ['CompagnyID', '==', companyId],
        where: [['Type', '==', 'personaSchedule'], ['PersonaID', 'in', personaIds]],
        initial: [],
    });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {'asc' | 'desc'} order
 * @returns {Array<T>}
 */
const useFirestoreNoCompagnyOrderBy = (collection, field, order) =>
    useFirestoreQuery(collection, { scope: 'none', orderBy: [field, order] });

const useFirestoreNoCompagnyGeneralOrderBy = (collection, field, id, fieldTwo, order) =>
    useFirestoreQuery(collection, {
        scope: 'none',
        where: [[field, '==', id]],
        orderBy: [fieldTwo, order],
    });

const useFirestoreNoCompagnyGeneralArrayContainsOrderBy = (collection, field, id, fieldTwo, idTwo, fieldThree, order) =>
    useFirestoreQuery(collection, {
        scope: 'none',
        where: [[field, '==', id], [fieldTwo, 'array-contains', idTwo]],
        orderBy: [fieldThree, order],
    });

const useFirestoreNoCompagnyGeneralTwoOrderBy = (collection, field, id, field2, id2, fieldTwo, order) =>
    useFirestoreQuery(collection, {
        scope: 'none',
        where: [[field, '==', id], [field2, '==', id2]],
        orderBy: [fieldTwo, order],
    });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {string | boolean} id
 * @returns {Array<T>}
 */
const useFirestoreNoCompagny = (collection, field, id) =>
    useFirestoreQuery(collection, { scope: 'none', where: [[field, '==', id]] });

/**
 * Als useFirestoreNoCompagny, maar geeft ook terug of de query faalde.
 *
 * Nodig voor cross-tenant kaarten: de Firestore-regels weigeren een read op
 * een omgeving waar de gebruiker geen lid van is, en zo'n weigering levert
 * geen fout op in de UI — de lijst blijft gewoon leeg. Wie het verschil moet
 * tonen tussen 'niets gevonden' en 'niet mogen zien' heeft de fout nodig.
 *
 * AI-CONTEXT: See docs/inbox.md Bug #133
 *
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {string} id
 * @returns {{docs: Array<T>|string, error: {code?: string}|null, loading: boolean}}
 */
const useFirestoreNoCompagnyWithStatus = (collection, field, id) => {
    const [error, setError] = useState(/** @type {{code?: string}|null} */ (null));

    // Een nieuwe vraag begint zonder oordeel: anders blijft de melding van het
    // vorige project staan terwijl het volgende nog laadt.
    useEffect(() => { setError(null); }, [collection, field, id]);

    const onError = useCallback((queryError) => { setError(queryError); }, []);
    const docs = useFirestoreQuery(collection, {
        scope: 'none',
        where: [[field, '==', id]],
        onError,
    });

    return { docs, error, loading: !Array.isArray(docs) && !error };
};

// Like useFirestoreNoCompagny, but adds an `array-contains` filter. Used to
// tenant-scope Synchronisations lookups that key off a non-tenant field.
// AI-CONTEXT: See docs/pitfalls.md#security-synchronisations-members-tenant-gate
const useFirestoreNoCompagnyGeneralArrayContains = (collection, field, id, arrayField, arrayValue) =>
    useFirestoreQuery(collection, {
        scope: 'none',
        where: [[field, '==', id], [arrayField, 'array-contains', arrayValue]],
    });

const useFirestoreNoCompagnyTwo = (collection, field, id, field2, id2) =>
    useFirestoreQuery(collection, {
        scope: 'none',
        where: [[field, '==', id], [field2, '==', id2]],
    });

const useFirestoreNoCompagnyThree = (collection, field, id, field2, id2, field3, id3) =>
    useFirestoreQuery(collection, {
        scope: 'none',
        where: [[field, '==', id], [field2, '==', id2], [field3, '==', id3]],
    });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {string | boolean} id
 * @returns {Array<T>}
 */
const useFirestoreGeneral = (collection, field, id) =>
    useFirestoreQuery(collection, { where: [[field, '==', id]] });

const useFirestoreGeneralIn = (collection, field, values) =>
    useFirestoreQuery(collection, { where: [[field, 'in', values]], initial: [] });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {string | boolean} id
 * @param {string} field2
 * @param {string | boolean} id2
 * @returns {Array<T>}
 */
const useFirestoreGeneralTwo = (collection, field, id, field2, id2) =>
    useFirestoreQuery(collection, {
        where: [[field, '==', id], [field2, '==', id2]],
    });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {string | boolean} id
 * @param {string} field2
 * @param {string | boolean} id2
 * @param {string} field3
 * @param {string | boolean} id3
 * @returns {Array<T>}
 */
const useFirestoreGeneralThree = (collection, field, id, field2, id2, field3, id3) =>
    useFirestoreQuery(collection, {
        where: [[field, '==', id], [field2, '==', id2], [field3, '==', id3]],
    });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {string | boolean} id
 * @param {string} field2
 * @param {string | boolean} id2
 * @param {string} field3
 * @param {string | boolean} id3
 * @param {string} field4
 * @param {string | boolean} id4
 * @returns {Array<T>}
 */
const useFirestoreGeneralFour = (collection, field, id, field2, id2, field3, id3, field4, id4) =>
    useFirestoreQuery(collection, {
        where: [[field, '==', id], [field2, '==', id2], [field3, '==', id3], [field4, '==', id4]],
    });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {string | boolean} id
 * @param {string} field2
 * @param {'asc' | 'desc'} order
 * @returns {Array<T>}
 */
const useFirestoreGeneralOrderBy = (collection, field, id, field2, order) =>
    useFirestoreQuery(collection, {
        where: [[field, '==', id]],
        orderBy: [field2, order],
    });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {string | boolean} id
 * @param {string} field2
 * @param {'asc' | 'desc'} order
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {Array<T>}
 */
const useFirestoreGeneralOrderByDates = (collection, field, id, field2, order, startDate, endDate) =>
    useFirestoreQuery(collection, {
        where: [[field, '==', id], ['Date', '>=', startDate], ['Date', '<=', endDate]],
        orderBy: [field2, order],
    });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {string | boolean} id
 * @param {string} field2
 * @param {string | boolean} id2
 * @param {string} field3
 * @param {'asc' | 'desc'} order
 * @returns {Array<T>}
 */
const useFirestoreGeneralTwoOrderBy = (collection, field, id, field2, id2, field3, order) =>
    useFirestoreQuery(collection, {
        where: [[field, '==', id], [field2, '==', id2]],
        orderBy: [field3, order],
    });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {string | boolean} id
 * @param {string} field2
 * @param {string | boolean} id2
 * @param {string} field3
 * @param {string | boolean} id3
 * @param {string} field4
 * @param {'asc' | 'desc'} order
 * @returns {Array<T>}
 */
const useFirestoreGeneralThreeOrderBy = (collection, field, id, field2, id2, field3, id3, field4, order) =>
    useFirestoreQuery(collection, {
        where: [[field, '==', id], [field2, '==', id2], [field3, '==', id3]],
        orderBy: [field4, order],
    });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {string | boolean} id
 * @param {string} field2
 * @param {'asc' | 'desc'} order
 * @returns {Array<T>}
 */
const useFirestoreArrayContainsOrderBy = (collection, field, id, field2, order) =>
    useFirestoreQuery(collection, {
        where: [[field, 'array-contains', id]],
        orderBy: [field2, order],
    });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {'asc' | 'desc'} order
 * @returns {Array<T>}
 */
const useFirestoreOrderBy = (collection, field, order) =>
    useFirestoreQuery(collection, { orderBy: [field, order] });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {string | boolean} id
 * @returns {Array<T>}
 */
const useFirestoreGeneralArrayContains = (collection, field, id) =>
    useFirestoreQuery(collection, { where: [[field, 'array-contains', id]] });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {string | boolean} id
 * @param {string} field2
 * @param {'asc' | 'desc'} order
 * @returns {Array<T>}
 */
const useFirestoreGeneralArrayContainsOrderBy = (collection, field, id, field2, order) =>
    useFirestoreQuery(collection, {
        where: [[field, 'array-contains', id]],
        orderBy: [field2, order],
    });

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {string | boolean} id
 * @param {string} field2
 * @param {string | boolean} id2
 * @param {string} field3
 * @param {'asc' | 'desc'} order
 * @returns {Array<T>}
 */
const useFirestoreGeneralTwoArrayContainsOrderBy = (collection, field, id, field2, id2, field3, order) =>
    useFirestoreQuery(collection, {
        where: [[field, '==', id], [field2, 'array-contains', id2]],
        orderBy: [field3, order],
    });

/**
 * @template T
 * @param {string} collection
 * @returns {Array<T>}
 */
const useFirestore = (collection) =>
    useFirestoreQuery(collection, { initial: [] });

// Same tenant-scoped listener as useFirestore, but also reports whether the
// first snapshot has returned. Lets a consumer tell "still loading" apart from
// "loaded, genuinely empty". Bespoke because it returns [docs, loaded] rather
// than a bare array — the soft-delete filter is applied inline here too.
const useFirestoreLoaded = (collection) => {

    const [docs, setDocs] = useState([])
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        // Geen organisatie in de url (de kale `/` na inloggen): niets op te
        // vragen. Zonder deze poort opende de PermissionsProvider, die bovenaan
        // de app altijd draait, een UserRoles-query op `CompagnyID == ''` die de
        // regels weigeren — een permission-denied in de console bij elke start
        // op `/`. Zelfde betekenis van '' als de missing-value guard van
        // useFirestoreQuery. `loaded` gaat wel op true: er komt niets meer, en
        // een consument mag niet in de laadstand blijven hangen.
        // AI-CONTEXT: See docs/pitfalls.md#database-firestore-null-is-a-valid-query-value
        if (client === '' || client === undefined) {
            setDocs([])
            setLoaded(true)
            return
        }

        setLoaded(false)

        const scope = [['CompagnyID', '==', client]]

        const unsub = db.collection(collection)
        .where("CompagnyID", "==", client)
        .onSnapshot(querySnapshot => {
            let docArray = []
            querySnapshot.forEach(doc => {
                const data = {...doc.data(), docid: doc.id}
                if (notDeleted(data)) docArray.push(data)
            })
            setDocs(docArray)
            setLoaded(true)
        }, (error) => {
            onQueryError(collection, scope)(error)
            // Zonder dit blijft `loaded` eeuwig false en hangt de consument in
            // de laadstand — een kapotte query hoort zichtbaar te zijn, niet
            // eindeloos te draaien.
            setLoaded(true)
        })

        return () => unsub();

    }, [collection])

    return [docs, loaded]
};

/**
 * @template T
 * @param {string} collection
 * @param {string} field
 * @param {Array<any>} array
 * @returns {Array<T>}
 */
const useFirestoreIn = (collection, field, array) =>
    useFirestoreQuery(collection, { where: [[field, 'in', array]], initial: [] });

/**
 * @template T
 * @param {string} collection
 * @param {string | boolean} id
 * @returns {Array<T>}
 */
const useFirestoreID = (collection, id) =>
    useFirestoreQuery(collection, { where: [['ID', '==', id]] });

/**
 * Retrieves documents sorted by Timestamp.
 * @template T
 * @param {string} collection
 * @param {'asc' | 'desc'} order
 * @returns {Array<T>}
 */
const useFirestoreTimestamp = (collection, order) =>
    useFirestoreQuery(collection, { orderBy: ['Timestamp', order] });

const useFirestoreIsNot = (collection, field, id) =>
    useFirestoreQuery(collection, { where: [[field, '!=', id]] });

const useFirestoreUser = (userID) =>
    useFirestoreQuery('Users', {
        scope: ['Compagny', 'array-contains', client],
        where: [['ID', '==', userID]],
    });

const useFirestoreUserNotApproved = (userID) =>
    useFirestoreQuery('Users', { scope: 'none', where: [['ID', '==', userID]] });

/**
 * Het eigen Users-document van de ingelogde gebruiker, ongeacht in welke
 * organisatie hij nu kijkt (een superadmin in andermans omgeving is daar geen
 * teamlid, en `useFirestoreUser` zou dan leeg blijven).
 * @template T
 * @param {string} userID - `Users.ID`; '' = nog niet bekend, dan geen listener
 * @returns {Array<T>}
 */
const useFirestoreUserByID = (userID) =>
    useFirestoreQuery('Users', { scope: 'none', where: [['ID', '==', userID]] });

const useFirestoreUsers = () =>
    useFirestoreQuery('Users', { scope: ['Compagny', 'array-contains', client] });

const useFirestoreUsersApproved = () =>
    useFirestoreQuery('Users', {
        scope: ['Compagny', 'array-contains', client],
        where: [['Approved', '==', true]],
    });

const useFirestoreNotApproved = () =>
    useFirestoreQuery('Users', {
        scope: ['Compagny', 'array-contains', client],
        where: [['Approved', '==', false]],
    });

const useFirestoreActivities = (goal) =>
    useFirestoreQuery('Activities', { where: [['GoalID', '==', goal]] });

const useFirestoreAdmins = (id) =>
    useFirestoreQuery('Admins', { where: [['UserID', '==', id]] });

const useFirestoreQuestionnaires = () =>
    useFirestoreQuery('Questionnaires', { scope: 'none' });

const useFirestoreQuestionnaireFields = (id) =>
    useFirestoreQuery('QuestionnaireFields', {
        where: [['QuestionnaireID', '==', id]],
        orderBy: ['Position', 'asc'],
    });

const useFirestoreQuestionnairesResponses = (id, moment) =>
    useFirestoreQuery('QuestionnairesResponses', {
        where: [['FieldID', '==', id], ['MomentID', '==', moment]],
        orderBy: ['Timestamp', 'asc'],
    });

const useFirestoreQuestionnairesResponsesMoments = (id) =>
    useFirestoreQuery('QuestionnairesResponses', { where: [['MomentID', '==', id]] });

const useFirestoreQuestionnairesResponsesResearch = (id, field) =>
    useFirestoreQuery('QuestionnairesResponses', {
        where: [['ResearchID', '==', id], ['FieldID', '==', field]],
    });

const useFirestoreOutputQuestionnaireFields = (id) =>
    useFirestoreQuery('QuestionnaireFields', { where: [['OutputID', '==', id]] });

const useFirestoreOutputs = (id) =>
    useFirestoreQuery('Outputs', { where: [['ActivityID', '==', id]] });

const useFirestoreOutputsOrderBy = (id, item, direction) =>
    useFirestoreQuery('Outputs', {
        where: [['ActivityID', '==', id]],
        orderBy: [item, direction],
    });

const useFirestoreResearch = (id) =>
    useFirestoreQuery('Research', { where: [['OutputID', '==', id]] });

const useFirestoreCompagny = (id) =>
    useFirestoreQuery('CompagnyMeta', { scope: ['CompagnyID', '==', id] });

const useFirestoreAnalysisCategories = (id) =>
    useFirestoreQuery('AnalysisCategories', { where: [['FieldID', '==', id]] });

const useFirestoreAnalysisWords = (field, moment, category) =>
    useFirestoreQuery('AnalysisWords', {
        where: [['FieldID', '==', field], ['MomentID', '==', moment], ['CategoryID', '==', category]],
    });

const useFirestoreAnalysisTotalWords = (category) =>
    useFirestoreQuery('AnalysisWords', { where: [['CategoryID', '==', category]] });

const useFirestoreAnalysisMultipleChoise = (input, field) =>
    useFirestoreQuery('QuestionnairesResponses', {
        where: [['FieldID', '==', field], ['Input', '==', input]],
    });

const useFirestoreAnalysisMultipleChoiseMoment = (moment, field, input) =>
    useFirestoreQuery('QuestionnairesResponses', {
        where: [['MomentID', '==', moment], ['FieldID', '==', field], ['Input', '==', input]],
    });

const useFirestoreImpactManagers = (name) =>
    useFirestoreQuery('ImpactManagers', { scope: 'none', where: [['Hostname', '==', name]] });

const useFirestoreKPI = () =>
    useFirestoreQuery('OutputEffects', {
        where: [['KPI', '==', 'true']],
        orderBy: ['Position', 'asc'],
    });

const useFirestoreLiveReportKPI = (id) =>
    useFirestoreQuery('OutputEffects', {
        where: [['KPI', '==', 'true'], ['ID', '==', id]],
    });

const useFirestoreQuestionnaireFieldName = (id) =>
    useFirestoreQuery('QuestionnaireFields', {
        scope: ['Compagny', '==', client],
        where: [['ID', '==', id]],
    });

const useFirestoreQuestionnaireFieldUpdate = () =>
    useFirestoreQuery('QuestionnaireFields', { scope: ['Compagny', '==', client] });



export {
    useFirestoreQuery,
    useFirestoreAll,
    useFirestoreAllOrderBy,
    useFirestoreAllArrayContains,
    useFirestoreAllID,
    useFirestoreAllIn,
    useFirestoreTasksForMembers,
    useFirestorePersonaAbsences,
    useFirestorePersonaSchedules,
    useFirestoreNoCompagnyOrderBy,
    useFirestoreNoCompagnyGeneralOrderBy,
    useFirestoreNoCompagnyGeneralArrayContainsOrderBy,
    useFirestoreNoCompagnyGeneralTwoOrderBy,
    useFirestoreNoCompagny,
    useFirestoreNoCompagnyWithStatus,
    useFirestoreNoCompagnyGeneralArrayContains,
    useFirestoreNoCompagnyTwo,
    useFirestoreNoCompagnyThree,
    useFirestoreGeneral,
    useFirestoreGeneralIn,
    useFirestoreGeneralTwo,
    useFirestoreGeneralThree,
    useFirestoreGeneralFour,
    useFirestoreGeneralOrderBy,
    useFirestoreGeneralOrderByDates,
    useFirestoreGeneralTwoOrderBy,
    useFirestoreGeneralThreeOrderBy,
    useFirestoreArrayContainsOrderBy,
    useFirestoreOrderBy,
    useFirestoreGeneralArrayContains,
    useFirestoreGeneralArrayContainsOrderBy,
    useFirestoreGeneralTwoArrayContainsOrderBy,
    useFirestore,
    useFirestoreLoaded,
    useFirestoreIn,
    useFirestoreID,
    useFirestoreTimestamp,
    useFirestoreIsNot,
    useFirestoreUser,
    useFirestoreUserNotApproved,
    useFirestoreUserByID,
    useFirestoreUsers,
    useFirestoreUsersApproved,
    useFirestoreNotApproved,
    useFirestoreActivities,
    useFirestoreAdmins,
    useFirestoreQuestionnaires,
    useFirestoreQuestionnaireFields,
    useFirestoreQuestionnairesResponses,
    useFirestoreQuestionnairesResponsesMoments,
    useFirestoreQuestionnairesResponsesResearch,
    useFirestoreOutputQuestionnaireFields,
    useFirestoreOutputs,
    useFirestoreOutputsOrderBy,
    useFirestoreResearch,
    useFirestoreCompagny,
    useFirestoreAnalysisCategories,
    useFirestoreAnalysisWords,
    useFirestoreAnalysisTotalWords,
    useFirestoreAnalysisMultipleChoise,
    useFirestoreAnalysisMultipleChoiseMoment,
    useFirestoreImpactManagers,
    useFirestoreKPI,
    useFirestoreLiveReportKPI,
    useFirestoreQuestionnaireFieldName,
    useFirestoreQuestionnaireFieldUpdate
}
